import loadable from "@loadable/component";
import React from "react";
import { useSelector } from "react-redux";
import { SoftSignupDialog } from "web/react/components/soft-signup-dialog/soft-signup-dialog";
import { shouldUseNewModal } from "web/react/components/soft-signup-dialog/soft-signup-utils";
import { ANDROID_SIGNUP_CAPTURE_TYPES } from "web/redux/ducks/customer-capture-overlay";
import { ReduxStoreState } from "web/redux/store";
import environment from "web/script/modules/environment";
import userProfiler from "web/script/modules/userprofiler";
import browser from "web/script/utils/browser";
import { CustomerCaptureOverlaySerializer } from "web/types/serializers";

const EmailCaptureOverlay = loadable(
    () =>
        import(
            /* webpackChunkName: "email-capture-overlay" */ "web/react/components/email-capture-overlay/email-capture-overlay"
        )
);

const EmailCaptureOverlayMembershipBenefits = loadable(
    () =>
        import(
            /* webpackChunkName: "email-membership-capture-overlay" */ "web/react/components/email-capture-overlay-membership-benefits/email-capture-overlay-membership-benefits"
        )
);

const EmailAppCaptureOverlay = loadable(
    () =>
        import(
            /* webpackChunkName: "email-app-capture-overlay" */ "web/react/components/email-app-capture-overlay/email-app-capture-overlay"
        )
);

const QRCodeOverlay = loadable(
    () =>
        import(
            /* webpackChunkName: "email-app-capture-overlay" */ "web/react/components/qrcode-overlay/qrcode-overlay"
        )
);

const USE_COMBINED_APP_EMAIL_MODAL = ["signup_du", "feeds_product_card"];

type CustomerCaptureOverlayProps = CustomerCaptureOverlaySerializer;

export function CustomerCaptureOverlay(props: CustomerCaptureOverlayProps): JSX.Element | null {
    const {
        open: isOpen,
        captureType,
        appDeeplinkPath,
        onCtaClick,
        blockType,
        analyticsEventLabel,
        componentType,
    } = useSelector((state: ReduxStoreState) => {
        return state.customerCaptureOverlay;
    });

    if (!captureType || !isOpen) {
        return null;
    }

    const stockAlertShouldUseEmailAppModal =
        captureType === "signup_oos_save" &&
        environment.getFeature("eac_oos_pdp_redesign") &&
        environment.getFeature("eac_oos_pdp_redesign") !== "variant_control";
    const useCombinedOverlay =
        browser.isSupportedMobileDevice &&
        !userProfiler.isLoggedIn() &&
        (USE_COMBINED_APP_EMAIL_MODAL.includes(captureType) || stockAlertShouldUseEmailAppModal);

    if (captureType === "qr_code") {
        return <QRCodeOverlay {...props} />;
    } else if (
        ANDROID_SIGNUP_CAPTURE_TYPES.includes(captureType) &&
        browser.isSupportedMobileDevice &&
        !stockAlertShouldUseEmailAppModal
    ) {
        return (
            <EmailCaptureOverlayMembershipBenefits
                overlayOpen={isOpen}
                captureType={captureType}
                {...props}
            />
        );
    } else if (useCombinedOverlay && captureType !== "signup_checkout_save") {
        return (
            <EmailAppCaptureOverlay
                appDeeplinkPath={appDeeplinkPath}
                onCtaClick={onCtaClick}
                {...props}
            />
        );
    } else if (!shouldUseNewModal(captureType, componentType)) {
        return <EmailCaptureOverlay onCtaClick={onCtaClick} {...props} />;
    }

    return (
        <SoftSignupDialog
            analyticsEventLabel={analyticsEventLabel}
            captureType={captureType}
            blockType={blockType}
            overlayOpen={isOpen}
        />
    );
}
